// Final wave 1B: one boundary around the whole app (mockup M1 B4). A crash
// shows a friendly reload screen instead of a white page, and posts a small
// report (page, message, time) that the System status card counts. Only the
// first path segment is sent, so public tokens never leave the browser.

import { Component, type ReactNode } from 'react';
import { HARDCODED_PRODUCER } from '../../shared/aiDraftPrompt';
import Button from './Button';
import Wordmark from './Wordmark';

/** Crew and client pages swap the reassurance line for the producer's mobile.
 *  They show no wordmark, because a white-label sheet can't be told apart
 *  once the page has crashed. */
const PUBLIC_SHEET_PAGES = ['/c', '/shoot', '/day', '/edit', '/review'];
const MAX_MESSAGE = 1000;

function currentPath(): string {
  return typeof window === 'undefined' ? '/' : window.location.pathname;
}

/** "/shoot/abc123" becomes "/shoot". */
export function reportPage(pathname: string): string {
  return '/' + (pathname.split(/[/?#]/)[1] ?? '').slice(0, 40);
}

export function isPublicSheetPage(pathname: string): boolean {
  return PUBLIC_SHEET_PAGES.includes(reportPage(pathname));
}

export function ReloadFallback({ pathname = currentPath() }: { pathname?: string }) {
  const publicPage = isPublicSheetPage(pathname);
  const first = HARDCODED_PRODUCER.name.split(' ')[0] || HARDCODED_PRODUCER.name;
  return (
    <main className="min-h-screen bg-page text-fg grid place-items-center px-5 py-12">
      <div role="alert" className="bk-card bk-errcard">
        {!publicPage && <Wordmark size="md" />}
        <p className={`bk-eyebrow${publicPage ? '' : ' mt-[18px]'}`}>Something went wrong</p>
        <h1>This page hit a <em>snag</em></h1>
        <p className="lead">
          {publicPage
            ? 'Reload to get back to your call sheet.'
            : 'Your saved work is safe. Reload to pick up where you left off.'}
        </p>
        <Button className="w-full" onClick={() => window.location.reload()}>Reload page</Button>
        <p className="foot">
          {publicPage
            ? <>Still stuck? Text {first} on <a href={`sms:${HARDCODED_PRODUCER.mobile.replace(/\s+/g, '')}`}>{HARDCODED_PRODUCER.mobile}</a>.</>
            : 'The error has been noted so it can be fixed.'}
        </p>
      </div>
    </main>
  );
}

interface Props { children?: ReactNode }
interface State { failed: boolean }

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: unknown) {
    const message = (error instanceof Error ? error.message : String(error)) || 'Unknown error';
    try {
      fetch('/api/client-errors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({ page: reportPage(currentPath()), message: message.slice(0, MAX_MESSAGE), time: Date.now() }),
      }).catch(() => {});
    } catch {
      // Reporting is best effort; the reload screen matters more.
    }
  }

  render() {
    return this.state.failed ? <ReloadFallback /> : this.props.children;
  }
}
